import logoWhite from '@assets/images/mandrake-logo-large-white.svg'
import useModalContext from "@contexts/modalContext"
import { useNavigate, useSearchParams } from "react-router-dom"
import BaseModal from "@elements/BaseModal"
import styled from '@emotion/styled'
import { colors, fontSize, size } from '@styles/theme'
import { FormTextField } from '@elements/Inputs'
import { Alert, Button, ButtonBase, LinearProgress, Popover } from '@mui/material'
import { PiArrowRightBold, PiInfo } from 'react-icons/pi'
import { useState } from 'react'
import { EyeMaskEndAdornment } from '@elements/InputAdornments'
import { PositionedTopProgress } from '@elements/Loaders'
import useTrigger from '@hooks/useTrigger'
import snakecaseKeys from 'snakecase-keys'
import axios from '@utils/webRequests'

const MandrakeLogo = styled.img`
  flex-shrink: 0;
  height: ${size(7)};
  width: ${size(7)};
`

const EmailPassForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: relative;

  width: 100%;
  height: 100%;
`

const EmailPassTitle = styled.h4`
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  margin: 0;
  // margin-bottom: ${size(3)};
  margin-top: ${size(2)};

  text-align: center;
  font-weight: 500;
  line-height: ${size(3)};
`

const ButtonArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  // margin-top: ${size(2)};
  flex-shrink: 0;
`

const NextButton = styled(ButtonBase)`
  width: 100%;
  padding: ${size(2)};
  border-radius: 100px;

  font-weight: 600;
  font-size: ${fontSize('md')};

  box-shadow: 0px 2px 8px 2px rgba(13, 64, 115, 0.70);
  background: linear-gradient(172.5deg, #2293EE 23.12%, #751DE4 110%);
`

const textFieldLabelProps = {
  style: { width: '100%' }
}

const textFieldInputStyles = {
  // backgroundColor: 'black',
  backgroundColor: 'rgba(255,255,255,0.1)',
  borderRadius: '100px',
}

const RequirementsText = styled.u`
  display: flex;
  align-items: center;
  margin-right: ${size(0.5)};
  color: ${colors.statusColors.chatting('light')};
`

// TODO: Put into elements for modular use, refactor, and replace in PasswordReset
const StyledAlert = ({ kind, children, AlertStyles }) => {
  const styles = {
    success: {
      backgroundColor: '#0C130D',
      color: '#CCE8CD',
    },
    info: {
      backgroundColor: '#071318',
      color: '#B8E7FB'
    },
    warning: {
      backgroundColor: '#191207',
      color: '#FFE2B7'
    },
    error: {
      backgroundColor: '#160B0B',
      color: '#F4C7C7'
    },
  }

  return (
    <Alert
      severity={kind}
      style={{
        width: '100%',
        height: size(8),
        borderRadius: 10,
        fontSize: fontSize('md'),
        marginTop: size(2),
        display: 'flex',
        alignItems: 'center',
        'MuiAlert-action': { padding: 0 },
        ...(styles[kind] || styles['success']),
        ...AlertStyles
      }}
    >
      {children}
    </Alert>
  )
}

// TODO: Put into auth components and refactor
const PasswordRequirementsAlert = () => (
  <StyledAlert kind='info' AlertStyles={{ height: 'unset' }}>
    Password must have:
    <ul style={{ margin: '0', padding: 0, listStyle: 'inside' }}>
      <li>{'> 5 characters'}</li>
      <li>One lowercase</li>
      <li>One uppercase</li>
      <li>
        One symbol
        <br />
        {"~`!@#$%^&*\(\)\-_+=\{\}\[\]|\;:\"'<>,.\/?"}
      </li>
    </ul>
  </StyledAlert>
)

const RegisterModalEmailPass = ({ formState, formErrors, setPasswordErrors, handleChange, actionHandler, isLoading, isLoadingDelayed }) => {
  const [isPasswordMasked, setIsPasswordMasked] = useState(true)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [searchParams] = useSearchParams()
  const [anchorEl, setAnchorEl] = useState(null)
  const isBlockingAccess = searchParams.get('blocked_access') || false
  const isRegisteringBeforeSubscribe = searchParams.get('register_before_subscribe') || false

  useTrigger(formState?.password, 500, () => {
    if (formState.password) {
      validateUserPassword()
    } else {
      setIsPasswordValid(false)
    }
  })

  const validateUserPassword = () => {
    axios.post(`/api/auth/account_validations`, snakecaseKeys(formState))
      .then((res) => {
        let passwordErrors = res.data?.password
        if (!passwordErrors) {
          setPasswordErrors(null)
          setIsPasswordValid(true)
        } else {
          setIsPasswordValid(false)
          setPasswordErrors(passwordErrors)
        }
      })
      .catch(console.log)
  }

  const handleRequirementsClick = (e) => {
    setAnchorEl(e.currentTarget)
  }

  const _handleSubmit = (e) => {
    e.preventDefault()
    if (isLoading) return;
    actionHandler(e)
  }

  const renderTitle = () => {
    if (isBlockingAccess) return(
      <>
        Access this feature
        <br />
        by registering
      </>
    ); else if(isRegisteringBeforeSubscribe) return (
      <>
        You'll need to register
        <br />
        before subscribing
      </>
    ); else return (
      'Register with Mandrake'
    )
  }

  const renderRequirementsButton = () => (
    <ButtonBase
      onClick={handleRequirementsClick}
      style={{ position: 'absolute', top: 0, right: 0 }}>
      <RequirementsText>
        <PiInfo />
        &nbsp;requirements
      </RequirementsText>
    </ButtonBase>
  )

  const renderPasswordAcceptableText = () => (
    <span style={{ color: colors.success(500) }}>password acceptable</span>
  )

  const renderPasswordHelperText = () => {
    if (isPasswordValid && !formErrors?.password) {
      return renderPasswordAcceptableText()
    } else if (formErrors?.password?.[0]) {
      return formErrors?.password?.[0]
    } else {
      return ' '
    }
  }

  return (
    <EmailPassForm onSubmit={_handleSubmit}>
      <Popover
        slotProps={{ paper: { style: { backgroundColor: 'transparent', marginTop: size(-2) } }  }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}>
        <PasswordRequirementsAlert />
      </Popover>
      <PositionedTopProgress isLoadingDelayed={isLoadingDelayed} />
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <MandrakeLogo src={logoWhite} />
        <EmailPassTitle>
          {renderTitle()}
        </EmailPassTitle>
      </div>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <FormTextField
          required
          labelProps={textFieldLabelProps}
          name='email'
          label='Email'
          type='email'
          placeholder='Your email'
          value={formState?.email}
          onChange={handleChange}
          error={formErrors?.email}
          helperText={formErrors?.email?.[0]}
          slotProps={{ input: {
            style: textFieldInputStyles
          }}}
        />
        <div style={{ position: 'relative', width: '100%' }}>
          {renderRequirementsButton()}
          <FormTextField
            required
            labelProps={textFieldLabelProps}
            name='password'
            label='Password'
            type={isPasswordMasked ? 'password' : 'text'}
            placeholder='Your password'
            value={formState?.password}
            onChange={handleChange}
            error={formErrors?.password}
            helperText={renderPasswordHelperText()}
            slotProps={{ input: {
              style: textFieldInputStyles,
              endAdornment: (
                <EyeMaskEndAdornment
                  isMasked={isPasswordMasked}
                  onClick={(e) => setIsPasswordMasked(!isPasswordMasked)}
                />
              ),
            }}}
          />
        </div>
      </div>
      <ButtonArea>
        <NextButton
          type='submit'
          variant='contained'>
          {isLoading
            ? 'Loading...'
            : <>Next&nbsp;<PiArrowRightBold /></>
          }
        </NextButton>
      </ButtonArea>
    </EmailPassForm>
  )
}

export default RegisterModalEmailPass
